import { Fragment, useEffect, useMemo, useState } from "react";
import { api, type Card, type Employee } from "../lib/api";
import { zaKontoWieExport } from "../lib/zaKonto";
import { useClientContext } from "../lib/ClientContext";
import { DayTable } from "../components/werkstatt/Phase4Berechnung";

/**
 * #319 (Birgit) — Auswertung pro Mitarbeiter, monatsweit + nachvollziehbar.
 * Beantwortet Birgits Frage „Wo kann ich kontrollieren, wie die Zahlen zustande
 * kommen?": pro Mitarbeiter die Monatssummen (Ist/Soll/Δ/ZA-Konto/Überstunden)
 * und aufklappbar die vollständige Tag-für-Tag-Herleitung (DayTable, wiederverwendet
 * aus der Werkstatt). Für admin/user zugänglich — NICHT developer-only.
 */
function n(v: unknown): string {
  const x = typeof v === "number" ? v : Number(v);
  return Number.isFinite(x) ? x.toLocaleString("de-AT", { maximumFractionDigits: 2 }) : "–";
}

export function AuswertungPage() {
  const { selectedClientId } = useClientContext();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  // Der Auszahlungsmodus entscheidet, was aufs ZA-Konto geht — ohne ihn zeigt
  // die Tabelle eine andere Zahl als der Export (Hotfix 13.08., siehe zaKonto.ts).
  const [employees, setEmployees] = useState<Employee[]>([]);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    api.cards
      .list(month, "", selectedClientId)
      .then((r) => setCards(r.cards.filter((c) => c.kind === "timesheet" && (c.data as any)?.calculation?.totals)))
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [month, selectedClientId]);

  useEffect(() => {
    api.employees.list(selectedClientId).then(setEmployees).catch(() => setEmployees([]));
  }, [selectedClientId]);

  const rows = useMemo(
    () =>
      cards
        .map((c) => {
          const calc = (c.data as any).calculation;
          const modus = employees.find((e) => e.id === c.employee_id)?.ueberstunden_pay_mode;
          return {
            c, t: calc.totals ?? {}, days: calc.days ?? [],
            za: zaKontoWieExport(calc, modus),
          };
        })
        .sort((a, b) =>
          `${a.c.last_name ?? ""} ${a.c.first_name ?? ""}`.localeCompare(
            `${b.c.last_name ?? ""} ${b.c.first_name ?? ""}`,
            "de",
          ),
        ),
    [cards, employees],
  );

  const toggle = (id: string) =>
    setOpen((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const cell = { padding: "6px 8px", textAlign: "right" as const, whiteSpace: "nowrap" as const };

  return (
    <div className="page-auswertung" style={{ padding: 16, maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 16, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0 }}>Auswertung</h2>
        <label style={{ fontSize: 14 }}>
          Monat: <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </label>
        <span className="muted" style={{ fontSize: 13 }}>
          {loading ? "Lade…" : `${rows.length} Mitarbeiter`}
        </span>
      </div>
      <p className="muted" style={{ fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
        Pro Mitarbeiter die Monatssummen. „Details" klappt die vollständige Tag-für-Tag-Herleitung auf —
        damit nachvollziehbar ist, wie die Zahlen zustande kommen.
      </p>

      {err && <p style={{ color: "#c13737" }}>Fehler: {err}</p>}
      {!loading && !err && rows.length === 0 && (
        <p className="muted">Keine berechneten Stundenlisten für {month}.</p>
      )}

      {rows.length > 0 && (
        <table style={{ width: "100%", marginTop: 12, borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ color: "#475569", borderBottom: "2px solid #e2e8f0" }}>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>Mitarbeiter</th>
              <th style={cell}>Ist</th>
              <th style={cell}>Soll</th>
              <th style={cell}>Δ</th>
              <th style={cell}>ZA-Konto</th>
              <th style={cell}>Üstd 50%</th>
              <th style={cell}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ c, t, days, za }) => {
              const isOpen = open.has(c.id);
              const diff = Number(t.diff_h);
              return (
                <Fragment key={c.id}>
                  <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "6px 8px" }}>
                      <strong>{c.last_name ?? "?"} {c.first_name ?? ""}</strong>
                      {c.dienstnehmernummer && (
                        <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>#{c.dienstnehmernummer}</span>
                      )}
                    </td>
                    <td style={cell}>{n(t.stunden_ist)}</td>
                    <td style={cell}>{n(t.stunden_soll)}</td>
                    <td style={{ ...cell, color: Number.isFinite(diff) && diff < 0 ? "#c13737" : "#15803d", fontWeight: 600 }}>
                      {n(t.diff_h)}
                    </td>
                    <td style={cell}>{n(za ?? t.ueberstunden_za_h)}</td>
                    <td style={cell}>{n(t.ueberstunden_50_total_h)}</td>
                    <td style={cell}>
                      <button type="button" className="link" onClick={() => toggle(c.id)}>
                        {isOpen ? "Details ▾" : "Details ▸"}
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={7} style={{ padding: "4px 8px 16px", background: "#f8fafc" }}>
                        <DayTable days={days} />
                        <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                          Mehrstunden (→ ZA-Konto) = Σ je Woche clamp(Ist−Soll, 0, 42−Soll) · Überstunden 50% = Σ je Woche
                          max(0, Ist−42) · Δ = Ist−Soll über den Monat.
                        </p>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
